import styled from "@emotion/styled";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";

import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "components/ui/chart/Chart";
import EmptyState from "components/ui/empty_state/EmptyState";
import CentredSpinner from "lib/core_components/CentredSpinner";
import { AppActivityDay } from "lib/hooks/api/usage/useCompanyAppActivity";
import { pluralise } from "lib/utils";

type Props = {
  /** One entry per day on the axis, oldest first, gaps already zero-filled. */
  days: AppActivityDay[];
  isLoading?: boolean;
};

const CHART_HEIGHT = 280;

const CONFIG: ChartConfig = {
  appUsers: {
    label: "App",
    color: "sysChart1",
    tooltipFormatter: (value: number) => pluralise(value, "user", true),
  },
  mcpUsers: {
    label: "MCP",
    color: "sysChart2",
    tooltipFormatter: (value: number) => pluralise(value, "user", true),
  },
};

/**
 * Users active per day, in the app and through MCP: a pair of bars per day, since each is its own
 * count and someone active both ways is in both.
 */
function AppActivityChart({ days, isLoading }: Props) {
  if (isLoading) {
    return (
      <ChartFrame>
        <CentredSpinner label="Loading activity chart" />
      </ChartFrame>
    );
  }

  if (days.every((day) => day.appUsers === 0 && day.mcpUsers === 0)) {
    return (
      <ChartFrame>
        <EmptyState
          type="noResults"
          heading="No activity in this period"
          description="Try a longer date range, or another team."
        />
      </ChartFrame>
    );
  }

  return (
    <StyledChartContainer config={CONFIG}>
      <BarChart accessibilityLayer data={days} margin={{ top: 8, right: 8 }} barGap={2}>
        <CartesianGrid vertical={false} />
        <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} minTickGap={32} />
        <YAxis width={40} tickLine={false} axisLine={false} allowDecimals={false} />
        <ChartTooltip
          content={
            <ChartTooltipContent labelFormatter={(_, payload) => payload[0]?.payload.longLabel} />
          }
        />
        <ChartLegend verticalAlign="bottom" content={<ChartLegendContent />} />
        <Bar dataKey="appUsers" fill="var(--color-appUsers)" radius={4} maxBarSize={24} />
        <Bar dataKey="mcpUsers" fill="var(--color-mcpUsers)" radius={4} maxBarSize={24} />
      </BarChart>
    </StyledChartContainer>
  );
}

const ChartFrame = styled.div({
  height: CHART_HEIGHT,
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const StyledChartContainer = styled(ChartContainer)({
  height: CHART_HEIGHT,
  width: "100%",
});

export default AppActivityChart;
